import { Delete, Edit, Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import ConfirmationDialog from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import { useDeleteProjectAdditionalInformationMutation } from "@repo/server-api";
import { useCurrencyFormatter, useNotification } from "@repo/shared/components";
import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { DialogProjectTeamMember } from "../dialogs";

import type { DialogProjectTeamMemberHandle } from "../dialogs";
import type { ProjectEditProps } from "./ProjectEditProps";
import type { GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import type { ConfirmationDialogHandle } from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import type { ProjectPosition, ProjectTeamMember } from "@repo/shared-project/types/ProjectTypes";

export const ProjectTeamMembersList = (props: ProjectEditProps) => {
	const { t } = useTranslation();
	const { project, setProject } = props;

	const formatCurrency = useCurrencyFormatter();

	const confirmationRef = useRef<ConfirmationDialogHandle | null>(null);
	const dialogProjectTeamMemberRef = useRef<DialogProjectTeamMemberHandle | null>(null);

	const { showNotification } = useNotification();

	const [deleteTeamMember] = useDeleteProjectAdditionalInformationMutation({
		fetchPolicy: "no-cache",
	});

	const addOrUpdatePosition = (teamMember: ProjectTeamMember) => {
		// If a new id has been generated, add the position to the list
		const existingIndex = project.teamMembers.findIndex((p) => p.id === teamMember.id) !== -1;

		// Sort positions after adding or updating
		const updatedTeamMembers = existingIndex
			? project.teamMembers.filter((p) => p.id !== teamMember.id)
			: [...project.teamMembers];
		updatedTeamMembers.push(teamMember);

		updatedTeamMembers.sort((a, b) =>
			(a.teamMember?.name ?? "").localeCompare(b.teamMember?.name ?? ""),
		);

		setProject((prev) => ({
			...prev,
			teamMembers: updatedTeamMembers,
		}));
	};

	const editTeamMemberInList = useCallback(
		(teamMember: ProjectTeamMember) => {
			dialogProjectTeamMemberRef.current?.show({
				projectId: props.project!.id,
				positions: project.positions,
				teamMember,
			});
		},
		[props.project, project.positions],
	);

	const deleteTeamMemberById = useCallback(
		(id: string) => {
			if (!project) return;

			deleteTeamMember({
				variables: {
					projectId: project.id,
					informationId: id,
				},
			}).then((res) => {
				if (res.data?.deleteProjectAdditionalInformation?.boolean !== true) {
					showNotification(t("error-while-deleting-member"), "error");
					return;
				}

				// Remove from local state
				setProject((prev) => ({
					...prev,
					teamMembers: prev.teamMembers.filter((member) => member.id !== id),
				}));

				showNotification(t("member-deleted"), "success");
			});
		},
		[project, deleteTeamMember, showNotification, t, setProject],
	);

	const columns: GridColDef<ProjectTeamMember>[] = useMemo(
		() => [
			{
				field: "displayName",
				headerName: t("name"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.teamMember?.name || "-";
				},
			},
			{
				field: "isFreelancer",
				headerName: t("is-freelancer"),
				width: 120,
				renderCell: (params: GridCellParams) => {
					return params.row.teamMember?.isFreelancer ? t("yes") : t("no");
				},
			},
			{
				field: "employedBy",
				headerName: t("employed-by"),
				width: 150,
				renderCell: (params: GridCellParams) => {
					return params.row.contractor?.name || "-";
				},
			},
			{
				field: "assignmentSummary",
				headerName: t("position-assignments"),
				width: 300,
				renderCell: (params: GridCellParams) => {
					const assignments = params.row.worksOnPositions || [];

					if (assignments.length === 0) {
						return "-";
					}

					return (
						<Box sx={{ py: 1 }}>
							{assignments.map(
								(assignment: {
									positionId: string;
									hours: number | null;
									pricePerHour: number | null;
								}) => {
									const position = project.positions.find((p) => p.id === assignment.positionId);
									const positionName = position?.service?.name || t("unknown-position");

									const detailsParts = [];
									if (assignment.hours != null) {
										detailsParts.push(`${assignment.hours.toFixed(2)}h`);
									}
									if (assignment.pricePerHour != null) {
										detailsParts.push(`${formatCurrency(assignment.pricePerHour)}/h`);
									}
									const details = detailsParts.length > 0 ? ` (${detailsParts.join(" @ ")})` : "";

									return (
										<Box key={assignment.positionId} sx={{ lineHeight: 1.4 }}>
											{positionName}
											{details}
										</Box>
									);
								},
							)}
						</Box>
					);
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				flex: 1,
				width: 150,
				renderCell: (params: GridCellParams) => {
					const openDelete = () =>
						confirmationRef.current?.show({
							text: t("delete-conformation-member"),
							onYes: () => {
								deleteTeamMemberById(params.row.id);
							},
						});

					const openEdit = () => {
						editTeamMemberInList(params.row);
					};

					return (
						<div>
							<Button
								data-testid="edit-button"
								onClick={() => {
									openEdit();
								}}>
								<Edit />
							</Button>
							<Button data-testid="delete-button" onClick={() => openDelete()}>
								<Delete />
							</Button>
						</div>
					);
				},
			},
		],
		[t, deleteTeamMemberById, editTeamMemberInList, formatCurrency, project.positions],
	);

	return (
		<Box sx={{ width: "100%" }}>
			<ConfirmationDialog ref={confirmationRef} />
			<DialogProjectTeamMember ref={dialogProjectTeamMemberRef} onSaved={addOrUpdatePosition} />

			<Box className="clear">
				<Button
					variant="contained"
					className="blueButton" sx={{ mb: 3 }}
					onClick={() => {
						dialogProjectTeamMemberRef.current?.show({
							projectId: props.project!.id,
							positions: project.positions,
						});
					}}>
					{t("create-new-member")}
					<AddIcon />
				</Button>
			</Box>
			<DataGridPremium
				getRowId={(row: ProjectTeamMember) => (row.id ? row.id : "")}
				rows={project.teamMembers}
				columns={columns}
				disableRowSelectionOnClick
				className="table"
				localeText={{
					noRowsLabel: t("no-members"),
				}}
			/>
		</Box>
	);
};
